/**
 * Error sanitization utilities to prevent database schema and implementation detail leakage.
 * Maps specific error codes and messages to generic user-friendly messages.
 */

interface SanitizedError {
  userMessage: string;
  logDetails?: string;
}

// Map of Supabase/PostgreSQL error codes to user-friendly messages
const ERROR_CODE_MAP: Record<string, string> = {
  'PGRST116': 'Resource not found',
  'PGRST301': 'Unable to process request',
  'PGRST204': 'Unable to process request',
  '23505': 'This record already exists',
  '23503': 'Unable to complete operation due to data constraints',
  '42501': 'You do not have permission to perform this action',
  '42P01': 'Service configuration error',
  '28P01': 'Authentication failed',
  'P0001': 'Operation not allowed',
};

// Patterns that indicate database structure - these should never reach users
const SENSITIVE_PATTERNS = [
  /relation "[\w_]+" does not exist/i,
  /column "[\w_]+" does not exist/i,
  /table "[\w_]+"/i,
  /schema "[\w_]+"/i,
  /function "[\w_]+"/i,
  /trigger "[\w_]+"/i,
  /index "[\w_]+"/i,
  /constraint "[\w_]+"/i,
  /PGRST\d+/i,
  /PostgreSQL/i,
  /pg_/i,
  /auth\./i,
  /public\./i,
  /storage\./i,
];

/**
 * Sanitizes database errors for user display.
 * Logs full error details for debugging while returning safe messages to users.
 */
export const sanitizeDBError = (error: any): SanitizedError => {
  const errorMessage = error?.message || error?.toString() || 'Unknown error';
  const errorCode = error?.code || '';

  // Check if error code has a mapped message
  if (errorCode && ERROR_CODE_MAP[errorCode]) {
    return {
      userMessage: ERROR_CODE_MAP[errorCode],
      logDetails: errorMessage,
    };
  }

  // Check for sensitive patterns that reveal database structure
  for (const pattern of SENSITIVE_PATTERNS) {
    if (pattern.test(errorMessage)) {
      return {
        userMessage: 'A service error occurred. Please try again.',
        logDetails: errorMessage,
      };
    }
  }

  // Network-related errors
  if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError')) {
    return {
      userMessage: 'Network connection error. Please check your connection.',
      logDetails: errorMessage,
    };
  }

  // Authentication errors
  if (errorMessage.toLowerCase().includes('auth') || errorMessage.toLowerCase().includes('token')) {
    return {
      userMessage: 'Authentication error. Please sign in again.',
      logDetails: errorMessage,
    };
  }

  // Rate limiting
  if (errorMessage.includes('rate limit') || errorMessage.includes('too many requests')) {
    return {
      userMessage: 'Too many requests. Please wait a moment.',
      logDetails: errorMessage,
    };
  }

  // Default - don't leak any potentially sensitive info
  return {
    userMessage: 'An error occurred. Please try again.',
    logDetails: errorMessage,
  };
};

/**
 * Logs error details server-side only (not shown to users).
 * In production, this should integrate with a proper logging service.
 */
export const logErrorSecurely = (context: string, error: any): void => {
  // Only log to console in development
  if (import.meta.env.DEV) {
    console.error(`[${context}]`, error);
  }
  
  // In production, errors should be sent to a proper logging service
  // Example: Sentry, DataDog, CloudWatch, etc.
  // This prevents sensitive information from appearing in client-side logs
};

/**
 * Safe error handler for toast notifications.
 * Returns a sanitized message suitable for user display.
 */
export const getToastError = (error: any): string => {
  return sanitizeDBError(error).userMessage;
};
